import { Typography, Stack, Collapse, Box } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useEffect, useRef } from 'react';

import DashboardChecklist from './DashboardChecklist';

import LaunchpadProgress from 'modules/common/components/LaunchpadProgress';
import useLaunchpadSchema from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema';
import {
    TaskStatus,
    LaunchpadItem,
    LaunchpadSubtask,
} from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import { FeatureGates } from 'modules/common/constants/featuregates';
import { useMember } from 'modules/common/queries/member';
import useStatsigGate from 'modules/common/useStatsigGate';
import BusinessSetupCards from 'modules/Dashboard/Overview/ProgressView/BusinessSetupCards';
import OnboardingCallBanner from 'modules/Dashboard/Overview/OnboardingCallBanner';
import TaskCarousel from 'modules/Tasks/TaskCarousel';

function LaunchpadTasks(): JSX.Element | null {
    const isRedesignV1 = useStatsigGate(FeatureGates.AccountingSetupRedesignV1);
    const { launchpadItems, isPendingLaunchpadTasks } = useLaunchpadSchema();
    const history = useHistory();
    const { setIsLaunchpadVisible, isLaunchpadVisible, setIsHeaderInBlendMode } = useLayoutContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const { data: member } = useMember();
    const isMemberTasksUiEnabled = useStatsigGate(FeatureGates.MemberTasksUiEnabled);

    const handleItemClick = (item: LaunchpadItem) => {
        if (item.cta_url) {
            if (item.is_cta_external) {
                window.open(item.cta_url, '_blank');
            } else {
                history.push(item.cta_url);
            }
        }
    };

    const handleSubtaskClick = (subtask: LaunchpadSubtask) => {
        if (subtask.cta_url) {
            if (subtask.is_cta_external) {
                window.open(subtask.cta_url, '_blank');
            } else {
                history.push(subtask.cta_url);
            }
        }
    };

    useEffect(() => {
        const allTasksCompleted = launchpadItems.every(
            (item) => item.allSubtasksCompleted || item.status === TaskStatus.COMPLETED
        );
        if (allTasksCompleted && isLaunchpadVisible) {
            setIsLaunchpadVisible(false);
            setIsHeaderInBlendMode(false);
            return;
        }

        if (launchpadItems.length > 0 && !allTasksCompleted && !isLaunchpadVisible) {
            setIsLaunchpadVisible(true);
            setIsHeaderInBlendMode(true);
        }
    }, [launchpadItems, isLaunchpadVisible, setIsLaunchpadVisible, setIsHeaderInBlendMode]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return () => {
                setIsHeaderInBlendMode(false);
            };
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                const isVisible = entry.isIntersecting;
                const hasTasks = launchpadItems.length > 0;
                // Set blend mode to true only when visible AND there are tasks
                if (isVisible && hasTasks) {
                    setIsHeaderInBlendMode(true);
                } else if (entry.boundingClientRect.height > 0) {
                    // Only set false when the element has fully rendered (height > 0).
                    // During Collapse animation the container has ~0 height and
                    // isIntersecting is unreliable — defer to Effect #1 in that case.
                    setIsHeaderInBlendMode(false);
                }
            },
            {
                threshold: 0.02, // Trigger when 2% of the element is visible
                rootMargin: '0px 0px 50px 0px', // Add some margin to trigger slightly before fully visible
            }
        );

        observer.observe(container);

        return () => {
            observer.disconnect();
            setIsHeaderInBlendMode(false);
        };
    }, [launchpadItems.length, setIsHeaderInBlendMode]);

    const memberFullName = member?.preferred_name || member?.first_name || '';

    if (!launchpadItems?.length || isPendingLaunchpadTasks) {
        return null;
    }

    return (
        <Box
            width="100%"
            sx={{
                maxWidth: {
                    xs: '100dvw',
                    xl: '1800px',
                }, // for 2k resolutions and up
                display: isLaunchpadVisible ? 'block' : 'none',
                marginBottom: isLaunchpadVisible ? 1 : '0px',
            }}
            ref={containerRef}>
            <Collapse in={isLaunchpadVisible}>
                <Stack
                    sx={{
                        p: {
                            xs: 3,
                            md: 4,
                            lg: 8,
                        },
                        paddingTop: {
                            xs: 2,
                            md: 2,
                            lg: 2,
                        },
                        width: '100%',
                        backgroundColor: 'cement.200',
                        borderBottomLeftRadius: 12,
                        borderBottomRightRadius: 12,
                    }}
                    direction="column"
                    spacing={3}>
                    <Box sx={{ py: 1, width: '100%', maxWidth: '900px', alignSelf: 'center' }}>
                        <Typography variant="h2" gutterBottom>
                            Welcome{memberFullName ? `, ${memberFullName}` : '!'}
                        </Typography>
                        <Typography variant="h2" gutterBottom color="text.secondary">
                            Let&apos;s finish your business setup.
                        </Typography>
                    </Box>

                    <OnboardingCallBanner />

                    {isMemberTasksUiEnabled && (
                        <TaskCarousel
                            surface="dashboard"
                            sx={{
                                width: { sm: '100%' },
                                maxWidth: { sm: 900 },
                                alignSelf: { sm: 'center' },
                            }}
                        />
                    )}

                    {isRedesignV1 ? (
                        <DashboardChecklist
                            items={launchpadItems}
                            onItemClick={handleItemClick}
                            onSubtaskClick={handleSubtaskClick}
                        />
                    ) : (
                        <LaunchpadProgress
                            items={launchpadItems}
                            onItemClick={handleItemClick}
                            onSubtaskClick={handleSubtaskClick}
                            inlineExpandToggle
                        />
                    )}

                    <BusinessSetupCards />
                </Stack>
            </Collapse>
        </Box>
    );
}

export default LaunchpadTasks;
